import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import ErrorBoundary from '@helpcenter/error-boundary/src/error-boundary';
import { isJsmPortalRovoIntegrationEnabled } from '@helpcenter/feature-flags';
import type { FieldFormProps } from '@helpcenter/field-types';
import { getCloudId } from '@helpcenter/meta';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { fetchQuery, graphql } from 'react-relay';
import type { State as AppState } from 'state';
import { getAccountId } from 'state/selectors/user';
import { FieldBase } from 'view/common/fields/field-base';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import ErrorIcon from '@atlaskit/icon/core/status-error';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { TRACK_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { getEntitlementIdFromAri } from '@atlassian/help-center-common-util/entitlement-ari';
import { isFetchNetworkOrClientError } from '@atlassian/help-center-common-util/fetch';
import { getRelayEnvironment } from '@atlassian/help-center-common-util/relay';
import { reportError } from '@atlassian/help-center-common-util/sentry';
import type { entitlementPickerQuery } from './__generated__/entitlementPickerQuery.graphql';
import { EntitlementPickerDumb } from './entitlement-picker-dumb';
import { entitlementPickerLoadExperience } from './experiences';
import messages from './messages';
import type { Option } from './types';

export const ENTITLEMENTS_QUERY = graphql`
	query entitlementPickerQuery($cloudId: ID!, $accountId: ID!) {
		customerService(cloudId: $cloudId)
			@required(action: THROW)
			@optIn(to: ["CustomerServiceProductsAndEntitlementsM1"]) {
			individualByAccountId(accountId: $accountId) @required(action: THROW) {
				__typename
				... on CustomerServiceIndividual {
					id @required(action: THROW)
					entitlementList @required(action: THROW) {
						id
						product {
							name
						}
					}
				}
				... on QueryError {
					message
				}
			}
		}
	}
`;

export interface Props extends FieldFormProps<string> {
	reporterId?: string;
	isNewReporter?: boolean;
	enableSubmitButton: () => void;
	disableSubmitButton: () => void;
	value?: string;
	defaultEntitlement?: string;
}

const EntitlementPickerMain = (props: Props) => {
	di(fetchQuery, getCloudId, useAnalyticsEvents, reportError);
	const { formatMessage } = useIntl();
	const {
		reporterId,
		onChange,
		isNewReporter,
		disableSubmitButton,
		enableSubmitButton,
		value,
		defaultEntitlement,
		...fieldProps
	} = props;
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const cloudId = getCloudId();
	const [prevReporterId, setPrevReporterId] = useState<string | undefined | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [entitlements, setEntitlements] = useState<Option[]>([]);
	const [error, setError] = useState(false);
	const [hasAppliedDefault, setHasAppliedDefault] = useState(false);
	// When the rovo integration FG is enabled, rely solely on the useEffect below to apply
	// defaultEntitlement once via onChange (single source of truth). This lets the user clear
	// the field without it being re-populated. When the FG is off, preserve the old fallback.
	const effectiveValue = isJsmPortalRovoIntegrationEnabled() ? value : value || defaultEntitlement;
	// Try to match by ID first, then by product name if no match
	let selectedEntitlement: Option | null =
		entitlements.find((entitlement) => entitlement.value === effectiveValue) || null;
	if (!selectedEntitlement && effectiveValue) {
		selectedEntitlement =
			entitlements.find(
				(entitlement) => entitlement.label.toLowerCase() === effectiveValue.toLowerCase(),
			) || null;
	}

	const updateSelectedEntitlement = useCallback(
		(option: Option | null, notFromUserInteraction?: boolean) => {
			onChange?.(props.id, option?.value || '', notFromUserInteraction);
		},
		[props.id, onChange],
	);

	const fetchEntitlements = useCallback(async () => {
		if (!reporterId) {
			return;
		}
		setIsLoading(true);
		disableSubmitButton();

		try {
			const data = await fetchQuery<entitlementPickerQuery>(
				getRelayEnvironment(),
				ENTITLEMENTS_QUERY,
				{
					cloudId,
					accountId: reporterId,
				},
			).toPromise();

			if (data?.customerService.individualByAccountId.__typename === 'QueryError') {
				throw new Error(
					data.customerService.individualByAccountId.message ?? 'entitlementPickerQuery failed',
				);
			}

			if (data?.customerService.individualByAccountId.__typename !== 'CustomerServiceIndividual') {
				throw new Error('entitlementPickerQuery failed');
			}

			const entitlementList = data.customerService?.individualByAccountId?.entitlementList;
			const entitlementOptions = entitlementList.map((entitlement) => ({
				label: entitlement.product.name,
				value: getEntitlementIdFromAri(entitlement.id),
			}));
			setEntitlements(entitlementOptions);

			createAnalyticsEvent({
				action: 'fetched',
				analyticsType: TRACK_EVENT_TYPE,
				actionSubject: 'entitlements',
				numberOfEntitlements: entitlementOptions.length,
			}).fire();
		} catch (e) {
			setEntitlements([]);
			setError(true);

			reportError(e, {
				teamName: 'boysenberry',
				errorBoundaryId: 'EntitlementPickerMain',
				errorBoundaryPackageName: 'jsd-portal-frontend',
			});

			if (isFetchNetworkOrClientError(e)) {
				entitlementPickerLoadExperience.abort() as unknown as void;
			} else {
				entitlementPickerLoadExperience.failure() as unknown as void;
			}
		} finally {
			updateSelectedEntitlement(null, true);
			setIsLoading(false);
			enableSubmitButton();
		}
	}, [
		cloudId,
		createAnalyticsEvent,
		disableSubmitButton,
		enableSubmitButton,
		reporterId,
		updateSelectedEntitlement,
	]);

	const onNewReporter = useCallback(() => {
		setPrevReporterId(reporterId);
		setError(false);
		entitlementPickerLoadExperience.start() as unknown as void;
	}, [reporterId]);

	const onNoEntitlements = useCallback(() => {
		setEntitlements([]);
		updateSelectedEntitlement(null, true);
		entitlementPickerLoadExperience.success() as unknown as void;
	}, [updateSelectedEntitlement]);

	useEffect(() => {
		if (reporterId === prevReporterId) {
			return;
		}
		onNewReporter();
		// reporterId is '' if the reporter field has been cleared
		// reporterID is undefined if the user is anonymous
		if (isNewReporter || reporterId === '' || reporterId === undefined) {
			onNoEntitlements();
			return;
		}
		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		fetchEntitlements();
	}, [
		cloudId,
		fetchEntitlements,
		isNewReporter,
		onNewReporter,
		onNoEntitlements,
		prevReporterId,
		reporterId,
		updateSelectedEntitlement,
	]);

	// When entitlements are loaded and defaultEntitlement is provided, select it.
	// When the rovo integration FG is enabled, only apply the default once so that
	// the user can clear the field without it being re-populated.
	useEffect(() => {
		const shouldApply = isJsmPortalRovoIntegrationEnabled() ? !hasAppliedDefault : true;
		if (shouldApply && defaultEntitlement && entitlements.length > 0) {
			// Try to match by ID first, then by product name if no match
			const matchedEntitlement =
				entitlements.find((entitlement) => entitlement.value === defaultEntitlement) ||
				entitlements.find(
					(entitlement) => entitlement.label.toLowerCase() === defaultEntitlement.toLowerCase(),
				);
			if (matchedEntitlement) {
				updateSelectedEntitlement(matchedEntitlement, true);
				setHasAppliedDefault(true);
			}
		}
	}, [defaultEntitlement, entitlements, hasAppliedDefault, updateSelectedEntitlement]);

	useEffect(() => {
		createAnalyticsEvent({
			action: 'viewed',
			analyticsType: TRACK_EVENT_TYPE,
			actionSubject: 'entitlementPicker',
		}).fire();
	}, [createAnalyticsEvent]);

	if (error) {
		return (
			<FieldBase label={fieldProps.label} htmlFor="" required={fieldProps.required}>
				<Box xcss={ErrorMessageStyles}>
					<ErrorIcon spacing="spacious" label="" color={token('color.icon.danger', '#c9372c')} />
					{formatMessage(messages.errorMessage)}
				</Box>
			</FieldBase>
		);
	}

	return (
		<EntitlementPickerDumb
			{...fieldProps}
			onChange={updateSelectedEntitlement}
			isLoading={isLoading}
			selectedEntitlement={selectedEntitlement}
			entitlements={entitlements}
		/>
	);
};

const EntitlementPickerWithErrorBoundary = (props: Props) => (
	<ErrorBoundary id="entitlement-picker" packageName="portal-fields">
		<EntitlementPickerMain {...props} />
	</ErrorBoundary>
);

const selector = createSelector(
	// if there is a reporter field in the form we use that value, otherwise we use the current user's account id
	(state: AppState, props: Props) =>
		props.reporterId === undefined ? getAccountId(state) : props.reporterId,
	(reporterId) => ({ reporterId }),
);

export const EntitlementPicker = connect(selector)(EntitlementPickerWithErrorBoundary);

const ErrorMessageStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '20px',
	gap: 'space.050',
	marginTop: 'space.100',
});
